import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Printer, CheckSquare, Square } from 'lucide-react';
import api from '../utils/api';
import { CLINIC_NAME, CLINIC_ADDRESS, CLINIC_PHONE, CLINIC_EMAIL } from '../utils/clinicConfig';

function fmt(n) { return (Number(n)||0).toLocaleString('en-IN', { maximumFractionDigits:2 }); }
function fmtDate(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  return new Date(Number(y),Number(m)-1,Number(d)).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BillPrintModal({ patient, onClose }) {
  // All visits for this patient (same patientUID)
  const [allVisits, setAllVisits]       = useState([]);
  const [loading, setLoading]           = useState(true);
  // Which visits to include in this bill
  const [selectedVisits, setSelectedVisits] = useState(new Set());
  // Pharmacy history for the patient
  const [pharmacyHistory, setPharmacyHistory] = useState([]);

  const loadAllVisits = useCallback(async () => {
    setLoading(true);
    try {
      const [patRes, pharmaRes] = await Promise.all([
        api.get('/patients', { params: { search: patient.patientUID, limit: 100 } }),
        api.get('/pharmacy/transactions', { params: { type: 'outgoing', search: patient.patientUID, limit: 100 } }),
      ]);
      const visits = (patRes.data.patients || []).sort((a,b) => (a.tokenDate||'') > (b.tokenDate||'') ? 1 : -1);
      setAllVisits(visits);
      setSelectedVisits(new Set([patient._id]));
      setPharmacyHistory(pharmaRes.data.transactions || []);
    } catch { setAllVisits([patient]); setSelectedVisits(new Set([patient._id])); }
    finally { setLoading(false); }
  }, [patient]);

  useEffect(() => { loadAllVisits(); }, [loadAllVisits]);

  // Manage the print-only container: a div appended directly to body
  // that holds a duplicate of the bill content. @media print hides
  // everything else and shows only this div — avoiding the fixed/overflow
  // stacking issues that cause blank pages from portal modals.
  const [printContainerEl] = React.useState(() => {
    const el = document.createElement('div');
    el.id = 'bill-print-only';
    document.body.appendChild(el);
    return el;
  });
  useEffect(() => {
    document.body.classList.add('print-bill-mode');
    return () => {
      document.body.classList.remove('print-bill-mode');
      if (printContainerEl.parentNode) printContainerEl.parentNode.removeChild(printContainerEl);
    };
  }, [printContainerEl]);

  function toggleVisit(id) {
    setSelectedVisits(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function selectAll()   { setSelectedVisits(new Set(allVisits.map(v => v._id))); }
  function selectNone()  { setSelectedVisits(new Set()); }

  const chosenVisits = allVisits.filter(v => selectedVisits.has(v._id));

  // Pharmacy transactions that belong to selected visit dates
  const chosenDates   = new Set(chosenVisits.map(v => v.tokenDate));
  const chosenPharmacy = pharmacyHistory.filter(t => chosenDates.has(t.date));

  // Billing totals across chosen visits
  // Use billing.amount as the source of truth for visit totals.
  // diagnosis[].finalPrice may be 0 if billing was set directly rather than
  // via the treatment catalog — in that case fall back to billing.amount.
  // Net billed per visit = (amount - discount) + tax
  // Mirrors the same formula used in BillingPage / PatientTrackerPage
  function netBilled(b) {
    if (!b) return 0;
    const after = Math.max((b.amount||0) - (b.discount||0), 0);
    return after + after * ((b.taxPercent||0) / 100);
  }
  const grandTotal  = chosenVisits.reduce((s,v) => {
    const fromDiag = v.diagnosis?.reduce((ds,d) => ds + (typeof d==='object' ? (Number(d.finalPrice)||0) : 0), 0) || 0;
    const fromBill = netBilled(v.billing);
    return s + (fromDiag > 0 ? fromDiag : fromBill);
  }, 0) + chosenPharmacy.reduce((s,t) => s + (t.sellingPrice||0)*t.quantity, 0);
  const totalPaid     = chosenVisits.reduce((s,v) => s + (v.billing?.amountPaid||0), 0);
  const totalPending  = Math.max(grandTotal - totalPaid, 0);

  // Bill number: BP + 6-digit patient sequence + date YYYYMMDD
  // Always generated fresh — e.g. BP00000120260716
  const billNumber = (() => {
    const seq  = (patient.patientUID || 'PT-000000').replace(/\D/g, '').padStart(6, '0');
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `BP${seq}${date}`;
  })();

  function doPrint() {
    const billEl = document.getElementById('bill-print-area');
    if (billEl && printContainerEl) {
      // Copy content only — let @page margin handle all spacing.
      // Strip the screen-only styles (box-shadow, fixed width, padding)
      // so the browser doesn't double-apply margins.
      printContainerEl.innerHTML = billEl.innerHTML;
      printContainerEl.style.cssText = 'display:block;width:100%;padding:0;margin:0;background:white;color:black;font-size:12px;line-height:1.5;';
    }
    const prev = document.title;
    document.title = CLINIC_NAME;
    window.print();
    document.title = prev;
    if (printContainerEl) { printContainerEl.innerHTML = ''; printContainerEl.style.cssText = ''; }
  }

  const modal = (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.65)', display:'flex', flexDirection:'column', alignItems:'center' }}
      onClick={onClose}>
      {/* Toolbar */}
      <div className="no-print" style={{ width:'100%', background:'#1e293b', color:'white', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}
        onClick={e => e.stopPropagation()}>
        <div>
          <div style={{ fontWeight:700, fontSize:14 }}>Print Bill — {patient.name}</div>
          <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
            {allVisits.length > 1 ? `${allVisits.length} visits found — select which to include` : 'Single visit'}
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-sm" style={{ background:'#2563eb', color:'white' }} onClick={doPrint}>
            <Printer size={13} /> Print Bill
          </button>
          <button className="btn btn-sm" style={{ background:'#475569', color:'white' }} onClick={onClose}>
            <X size={13} /> Close
          </button>
        </div>
      </div>

      {/* Visit selector — only shown when multiple visits exist */}
      {!loading && allVisits.length > 1 && (
        <div className="no-print" style={{ width:'210mm', background:'#f8fafc', padding:'12px 16px', borderBottom:'1px solid #e2e8f0', flexShrink:0 }}
          onClick={e => e.stopPropagation()}>
          <div style={{ fontWeight:700, fontSize:12, color:'#475569', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>Select Visits to Include:</span>
            <div style={{ display:'flex', gap:6 }}>
              <button className="btn btn-sm btn-outline" onClick={selectAll} style={{ fontSize:11 }}>All</button>
              <button className="btn btn-sm btn-outline" onClick={selectNone} style={{ fontSize:11 }}>None</button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:6 }}>
            {allVisits.map((v, vi) => {
              const checked = selectedVisits.has(v._id);
              return (
                <label key={v._id} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:6, border:`1px solid ${checked ? '#2563eb' : '#e2e8f0'}`, background: checked ? '#eff6ff' : 'white', cursor:'pointer', fontSize:12 }}>
                  {checked ? <CheckSquare size={14} color="#2563eb" /> : <Square size={14} color="#94a3b8" />}
                  <input type="checkbox" checked={checked} onChange={() => toggleVisit(v._id)} style={{ display:'none' }} />
                  <div>
                    <div style={{ fontWeight:700, color: checked ? '#2563eb' : '#1e293b' }}>Visit {vi+1}</div>
                    <div style={{ color:'#64748b' }}>{fmtDate(v.tokenDate)}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Bill preview — the inner div is what gets copied to the print container */}
      <div style={{ flex:1, overflowY:'auto', width:'100%', display:'flex', justifyContent:'center', padding:'20px 0' }}
        onClick={e => e.stopPropagation()}>
        {/* Screen-only wrapper — gives A4 shadow appearance. NOT copied to print container. */}
        <div style={{ background:'white', width:'210mm', minHeight:'297mm', boxShadow:'0 4px 32px rgba(0,0,0,0.4)' }}>
          {/* #bill-print-area: content only — no box-shadow or fixed width so
              the print container renders cleanly with just @page margins */}
          <div id="bill-print-area" style={{ padding:'14mm 16mm', color:'#111', fontSize:12, lineHeight:1.5 }}>

          {/* Clinic header */}
          <div style={{ textAlign:'center', marginBottom:16, borderBottom:'2px solid #1e293b', paddingBottom:12 }}>
            <div style={{ fontSize:22, fontWeight:900, color:'#1e293b', letterSpacing:'-0.02em' }}>{CLINIC_NAME}</div>
            {(CLINIC_ADDRESS || CLINIC_PHONE || CLINIC_EMAIL) && (
              <table style={{ width:'100%', borderCollapse:'collapse', marginTop:6 }}>
                <tbody>
                  <tr>
                    {CLINIC_ADDRESS && (
                      <td style={{ fontSize:11, color:'#475569', textAlign:'center', padding:'1px 8px' }}>
                        📍&nbsp;{CLINIC_ADDRESS}
                      </td>
                    )}
                  </tr>
                  <tr>
                    {CLINIC_PHONE && (
                      <td style={{ fontSize:11, color:'#475569', textAlign:'center', padding:'1px 8px' }}>
                        📱&nbsp;{CLINIC_PHONE}
                        {CLINIC_EMAIL && <>&nbsp;&nbsp;✉&nbsp;{CLINIC_EMAIL}</>}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            )}
            <div style={{ fontSize:11, color:'#64748b', marginTop:6, fontWeight:600, letterSpacing:'0.05em' }}>PATIENT BILL</div>
          </div>

          {/* Bill meta */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, marginBottom:14, fontSize:11 }}>
            <div><span style={{ color:'#64748b' }}>Bill No: </span><strong>{billNumber}</strong></div>
            <div style={{ textAlign:'right' }}><span style={{ color:'#64748b' }}>Date: </span><strong>{new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</strong></div>
          </div>

          {/* Patient details — table layout for print reliability */}
          <div style={{ marginBottom:14, padding:'10px 14px', border:'1px solid #e2e8f0', borderRadius:6 }}>
            <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>{patient.name}</div>
            <table style={{ fontSize:11, color:'#475569', borderCollapse:'collapse', width:'100%' }}>
              <tbody>
                <tr>
                  <td style={{ padding:'1px 12px 1px 0', fontFamily:'monospace', fontWeight:700, color:'#2563eb', whiteSpace:'nowrap' }}>{patient.patientUID}</td>
                  {patient.age && <td style={{ padding:'1px 12px 1px 0', whiteSpace:'nowrap' }}>{patient.age}y,&nbsp;{patient.gender}</td>}
                  {patient.phone && <td style={{ padding:'1px 12px 1px 0', whiteSpace:'nowrap' }}>📱&nbsp;{patient.phone}</td>}
                  {patient.address && <td style={{ padding:'1px 0', whiteSpace:'nowrap' }}>📍&nbsp;{patient.address}</td>}
                </tr>
              </tbody>
            </table>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', color:'#64748b', padding:20 }}>Loading visits…</div>
          ) : chosenVisits.length === 0 ? (
            <div style={{ textAlign:'center', color:'#64748b', padding:20 }}>No visits selected.</div>
          ) : (
            <>
              {/* Visit sections */}
              {chosenVisits.map((v, vi) => {
                const vDiag = Array.isArray(v.diagnosis) ? v.diagnosis : [];
                const vPharm = pharmacyHistory.filter(t => t.date === v.tokenDate);
                const vDiagTotal  = vDiag.reduce((s,d) => s+(typeof d==='object'?(Number(d.finalPrice)||0):0), 0);
                const vPharmTotal = vPharm.reduce((s,t) => s+(t.sellingPrice||0)*t.quantity, 0);
                const vBilled     = netBilled(v.billing);
                const vPaid       = v.billing?.amountPaid || 0;
                const vPend       = Math.max(vBilled - vPaid, 0);
                // Use diagnosis total if available, else fall back to net billed amount
                const vTreatTotal = vDiagTotal > 0 ? vDiagTotal : vBilled;

                return (
                  <div key={v._id} style={{ marginBottom:14, border:'1px solid #e2e8f0', borderRadius:6, overflow:'hidden', breakInside:'avoid' }}>
                    {/* Visit header — table for print spacing */}
                    <div style={{ padding:'8px 12px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>
                          <tr>
                            <td style={{ verticalAlign:'top' }}>
                              <span style={{ fontWeight:700, fontSize:13 }}>Visit {vi+1}</span>
                              <span style={{ color:'#64748b', fontSize:11, marginLeft:8 }}>{fmtDate(v.tokenDate)}</span>
                              {v.appointmentSlot && <span style={{ color:'#2563eb', fontSize:11, marginLeft:8 }}>🕐&nbsp;{v.appointmentSlot}</span>}
                              {v.billing?.invoiceNumber && <span style={{ color:'#64748b', fontSize:11, marginLeft:8 }}>Inv:&nbsp;{v.billing.invoiceNumber}</span>}
                              {v.doctorName && <span style={{ fontSize:11, color:'#475569', marginLeft:8 }}>{v.doctorName && (/^dr\.?\s/i.test(v.doctorName) ? v.doctorName : 'Dr. ' + v.doctorName)}</span>}
                            </td>
                            {v.reasonForVisit && <td style={{ textAlign:'right', fontSize:11, color:'#64748b', fontStyle:'italic', verticalAlign:'top', paddingLeft:8 }}>{v.reasonForVisit}</td>}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Treatments */}
                    {vDiag.length > 0 && (
                      <div style={{ padding:'8px 12px', borderBottom:'1px solid #f1f5f9' }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#64748b', marginBottom:5, textTransform:'uppercase' }}>Treatments</div>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                          <thead>
                            <tr style={{ color:'#94a3b8' }}>
                              <th style={{ textAlign:'left', padding:'2px 0', fontWeight:600 }}>Treatment</th>
                              <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Teeth</th>
                              <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Qty</th>
                              <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Rate</th>
                              <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Disc</th>
                              <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vDiag.map((d, di) => (
                              <tr key={di} style={{ borderTop:'1px solid #f1f5f9' }}>
                                <td style={{ padding:'3px 0', fontWeight:600 }}>{typeof d === 'string' ? d : d.name}</td>
                                <td style={{ textAlign:'right', padding:'3px 0', color:'#2563eb', fontSize:10 }}>
                                  {typeof d === 'object' && d.fdiRequired && Array.isArray(d.teethForTreatment) && d.teethForTreatment.length > 0
                                    ? d.teethForTreatment.map(t => `#${t}`).join(', ')
                                    : '—'}
                                </td>
                                <td style={{ textAlign:'right', padding:'3px 0', color:'#64748b' }}>{typeof d==='object'?(d.quantity||1):'1'}</td>
                                <td style={{ textAlign:'right', padding:'3px 0', color:'#64748b' }}>{typeof d==='object' && (d.catalogPrice||0)>0 ? `₹${fmt(d.catalogPrice)}` : '—'}</td>
                                <td style={{ textAlign:'right', padding:'3px 0', color:'#d97706' }}>{typeof d==='object' && (d.discountPercent||0)>0 ? `${d.discountPercent}%` : '—'}</td>
                                <td style={{ textAlign:'right', padding:'3px 0', fontWeight:700, color:'#16a34a' }}>{typeof d==='object' ? `₹${fmt(d.finalPrice)}` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                          {vTreatTotal > 0 && (
                            <tfoot>
                              <tr>
                                <td colSpan={5} style={{ textAlign:'right', padding:'4px 0', fontWeight:700, fontSize:11, color:'#475569' }}>Treatment Subtotal</td>
                                <td style={{ textAlign:'right', padding:'4px 0', fontWeight:800, color:'#16a34a' }}>₹{fmt(vDiagTotal > 0 ? vDiagTotal : vBilled)}</td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    )}

                    {/* Pharmacy items for this visit date */}
                    {vPharm.length > 0 && (
                      <div style={{ padding:'8px 12px', borderBottom:'1px solid #f1f5f9' }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#64748b', marginBottom:5, textTransform:'uppercase' }}>Pharmacy</div>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                          <tbody>
                            {vPharm.map(t => (
                              <tr key={t._id} style={{ borderTop:'1px solid #f1f5f9' }}>
                                <td style={{ padding:'3px 0', fontWeight:600 }}>{t.itemName}</td>
                                <td style={{ textAlign:'right', padding:'3px 0', color:'#64748b' }}>×{t.quantity} {t.unit}</td>
                                <td style={{ textAlign:'right', padding:'3px 0', fontWeight:700, color:'#16a34a' }}>₹{fmt((t.sellingPrice||0)*t.quantity)}</td>
                              </tr>
                            ))}
                          </tbody>
                          {vPharmTotal > 0 && (
                            <tfoot>
                              <tr>
                                <td colSpan={2} style={{ textAlign:'right', padding:'4px 0', fontWeight:700, fontSize:11, color:'#475569' }}>Pharmacy Subtotal</td>
                                <td style={{ textAlign:'right', padding:'4px 0', fontWeight:800, color:'#16a34a' }}>₹{fmt(vPharmTotal)}</td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    )}

                    {/* Visit billing summary — table for print stability */}
                    <table style={{ width:'100%', borderCollapse:'collapse', padding:'6px 12px', fontSize:11, background:'#f8fafc' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding:'5px 12px', color:'#64748b' }}>Visit Total:&nbsp;<strong>₹{fmt(netBilled(v.billing))}</strong></td>
                          <td style={{ padding:'5px 12px', color:'#16a34a', textAlign:'center' }}>Paid:&nbsp;<strong>₹{fmt(vPaid)}</strong></td>
                          <td style={{ padding:'5px 12px', color: vPend>0 ? '#d97706' : '#94a3b8', textAlign:'right' }}>{vPend > 0 ? <>Due:&nbsp;<strong>₹{fmt(vPend)}</strong></> : 'Settled'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}

              {/* Grand totals — table layout for print reliability */}
              <div style={{ marginTop:10, background:'#1e293b', borderRadius:6, color:'white', overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', textAlign:'center' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding:'10px 14px', width:'33%', borderRight:'1px solid #334155' }}>
                        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:4 }}>GRAND TOTAL</div>
                        <div style={{ fontSize:16, fontWeight:800, color:'white' }}>₹{fmt(grandTotal)}</div>
                      </td>
                      <td style={{ padding:'10px 14px', width:'33%', borderRight:'1px solid #334155' }}>
                        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:4 }}>TOTAL PAID</div>
                        <div style={{ fontSize:16, fontWeight:800, color:'#4ade80' }}>₹{fmt(totalPaid)}</div>
                      </td>
                      <td style={{ padding:'10px 14px', width:'33%' }}>
                        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:4 }}>BALANCE DUE</div>
                        <div style={{ fontSize:16, fontWeight:800, color: totalPending>0 ? '#fbbf24' : '#4ade80' }}>₹{fmt(totalPending)}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div style={{ marginTop:20, paddingTop:10, borderTop:'1px solid #e2e8f0', fontSize:10, color:'#94a3b8', display:'flex', justifyContent:'space-between' }}>
                <span>{CLINIC_NAME} — Thank you for your visit</span>
                <span>Bill No: {billNumber}</span>
              </div>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
